import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { RouteQueryComposable as TicketsRouteQuery } from '@/composables/routeQueries/useTicketsRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { TicketsStatusItemsValue } from '@/constants/fieldItems';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import {
  fetchGetTicketDashboardAll,
  fetchGetTicketDashboardUnread,
} from '@/helpers/fetchers';
import { ticketsStatusNumValues } from '@/constants/fieldItems';
import * as routeNames from '@/constants/routeNames';

export interface TicketsDashboardViewDataComposable {
  isFetchingJobTicketsData: Readonly<Ref<boolean>>;

  jobTicketsData: Readonly<
    Ref<
      | ReadonlyDeep<
          | openapi.GetTicketDashboardAll_200
          | openapi.GetTicketDashboardUnread_200
        >
      | undefined
    >
  >;

  jobTicketsDataError: Readonly<Ref<unknown | undefined>>;

  refreshJobTicketsData: () => Promise<void>;

  reload: () => TicketsDashboardViewDataComposable;
}

export default function useTicketsDashboardViewData(
  routeQuery: TicketsRouteQuery,
): TicketsDashboardViewDataComposable {
  const route = useRoute();

  const loaderStore = useLoaderStore();

  let jobTicketsDataController: AbortController | undefined;

  const jobTicketsDataFetchCounter = ref(0);

  const isFetchingJobTicketsData = computed(
    () => !!jobTicketsDataFetchCounter.value,
  );

  const jobTicketsData =
    ref<
      ReadonlyDeep<
        openapi.GetTicketDashboardAll_200 | openapi.GetTicketDashboardUnread_200
      >
    >();

  const jobTicketsDataError = ref();

  function clear(): void {
    if (jobTicketsDataController) jobTicketsDataController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchJobTicketsData(): Promise<
    | ReadonlyDeep<
        openapi.GetTicketDashboardAll_200 | openapi.GetTicketDashboardUnread_200
      >
    | undefined
  > {
    if (jobTicketsDataController) jobTicketsDataController.abort();
    const filterIDs = routeQuery.status.value?.map((statusName) => {
      const _statusName: TicketsStatusItemsValue =
        statusName as TicketsStatusItemsValue;
      return ticketsStatusNumValues[_statusName];
    });
    const endpointParams = {
      search: routeQuery.search.value,
      exclude_states: filterIDs,
      is_rfc: routeQuery.isRfc.value,
      page: routeQuery.curPage.value,
      page_size: routeQuery.pageSize.value,
    };
    jobTicketsDataController = new AbortController();
    let promise;
    if (routeQuery.isUnread.value) {
      promise = fetchGetTicketDashboardUnread(endpointParams, {
        signal: jobTicketsDataController.signal,
      });
    } else {
      promise = fetchGetTicketDashboardAll(endpointParams, {
        signal: jobTicketsDataController.signal,
      });
    }
    return await promise
      .then(function (data) {
        jobTicketsDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        jobTicketsDataError.value = err;
        throw err;
      });
  }

  async function refreshJobTicketsData(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    jobTicketsDataFetchCounter.value++;
    try {
      jobTicketsData.value = await _fetchJobTicketsData();
    } finally {
      jobTicketsDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  function reload(): TicketsDashboardViewDataComposable {
    void Promise.all([refreshJobTicketsData()]);
    return {
      isFetchingJobTicketsData,
      jobTicketsData,
      jobTicketsDataError,
      refreshJobTicketsData,
      reload,
    };
  }

  watch(
    [() => route.name, () => route.query],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.JOB_TICKETS) return;
      reload();
    },
    { immediate: true },
  );

  return {
    isFetchingJobTicketsData,
    jobTicketsData,
    jobTicketsDataError,
    refreshJobTicketsData,
    reload,
  };
}
